/**
 * Simple S3 file upload using presigned URL
 */

export type UploadOptions = {
  timeout?: number;
};

type UploadParams = {
  presignedUrl: string;
  file: File;
  options?: UploadOptions;
};

/**
 * Upload a file directly to S3 using a presigned URL
 */

export const DEFAULT_TIMEOUT = 600000; // 10 minutes

export async function uploadFileToS3({ presignedUrl, file, options = {} }: UploadParams): Promise<void> {
  const { timeout = DEFAULT_TIMEOUT } = options;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Setup timeout
    xhr.timeout = timeout;

    // Setup completion handlers
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed due to network error'));
    });

    xhr.addEventListener('timeout', () => {
      reject(new Error('Upload timed out'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload was aborted'));
    });

    // Send the file
    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.send(file);
  });
}
